import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Typography, Box, ToggleButtonGroup, ToggleButton, Skeleton } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { paperTradingApi, PortfolioValue, PaperTrade, PaperAccount } from '../../../services/paperTradingApi';

type TimeRange = '24h' | '7d' | '30d';

const STORAGE_KEY = 'btrader_portfolio_chart_range';
const RANGE_HOURS: Record<TimeRange, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };

// Letzte Auswahl aus localStorage holen, mit Fallback auf 24h.
const loadStoredRange = (): TimeRange => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === '24h' || v === '7d' || v === '30d') return v;
  } catch {
    // localStorage nicht verfuegbar (z.B. Privacy-Mode) — Fallback nehmen
  }
  return '24h';
};

const PortfolioChart: React.FC = () => {
  const [portfolio, setPortfolio] = useState<PortfolioValue | null>(null);
  const [account, setAccount] = useState<PaperAccount | null>(null);
  const [trades, setTrades] = useState<PaperTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>(loadStoredRange);

  // Auswahl bei Wechsel speichern, damit sie nach Reload erhalten bleibt.
  const handleRangeChange = (_: unknown, v: TimeRange | null) => {
    if (!v) return;
    setTimeRange(v);
    try {
      localStorage.setItem(STORAGE_KEY, v);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    let active = true;
    const fetchAll = () => {
      Promise.all([
        paperTradingApi.getPortfolio(),
        paperTradingApi.getAccount(),
        paperTradingApi.getTrades(500),
      ])
        .then(([pf, acc, tr]) => {
          if (!active) return;
          setPortfolio(pf);
          setAccount(acc);
          setTrades(Array.isArray(tr) ? tr : []);
        })
        .catch(console.error)
        .finally(() => {
          if (active) setLoading(false);
        });
    };
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  // Equity-Kurve: rueckwaerts vom aktuellen Portfoliowert. Pro Verkauf
  // mit realized_pnl ergibt sich ein Stuetzpunkt. Vor allen Trades steht
  // der initial_balance_eur als Startpunkt. So endet die Kurve immer auf
  // dem aktuellen total_eur und stimmt mit der Anzeige oben ueberein.
  const chartData = useMemo(() => {
    if (!portfolio || !account) return [];

    const now = Date.now();
    const cutoff = now - RANGE_HOURS[timeRange] * 3600_000;

    // Trades aufsteigend nach Zeit sortieren, nur die mit Zeitstempel.
    // Defensive: bei API-Fehler (z.B. 401) koennte trades unerwartet
    // ein Objekt sein; Array.isArray-Check verhindert "u is not iterable".
    const tradesArray = Array.isArray(trades) ? trades : [];
    const sorted = [...tradesArray]
      .filter((t) => t.created_at)
      .sort((a, b) => new Date(a.created_at!).getTime() - new Date(b.created_at!).getTime());

    // Rueckwaerts laufen: jeder Trade subtrahiert sein realized_pnl
    // (negativ subtrahieren = addieren bei Verlust). So entstehen die
    // Equity-Werte VOR dem jeweiligen Trade.
    const reversed: { ts: number; equity: number }[] = [];
    let equity = portfolio.total_eur;
    reversed.push({ ts: now, equity });
    for (let i = sorted.length - 1; i >= 0; i--) {
      const t = sorted[i];
      equity -= t.realized_pnl ?? 0;
      reversed.push({ ts: new Date(t.created_at!).getTime(), equity });
    }

    // Plus Startpunkt bei Konto-Anlage mit Initial Balance
    const accountStart = account.created_at ? new Date(account.created_at).getTime() : null;
    if (accountStart !== null) {
      reversed.push({ ts: accountStart, equity: account.initial_balance_eur });
    }

    // Auf Zeitfenster filtern und chronologisch ordnen
    const inRange = reversed
      .filter((p) => p.ts >= cutoff)
      .sort((a, b) => a.ts - b.ts);

    // Wenn Zeitfenster keinen Trade enthaelt, mit zwei Punkten arbeiten:
    // ein cutoff-Punkt (Equity am Anfang des Fensters = aktueller Wert,
    // weil sich seither nichts geaendert hat) und der aktuelle Punkt.
    if (inRange.length < 2) {
      return [
        { time: new Date(cutoff).toISOString(), value: portfolio.total_eur },
        { time: new Date(now).toISOString(), value: portfolio.total_eur },
      ];
    }

    return inRange.map((p) => ({
      time: new Date(p.ts).toISOString(),
      value: Number(p.equity.toFixed(2)),
    }));
  }, [portfolio, account, trades, timeRange]);

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const pnlColor = (portfolio?.pnl_eur ?? 0) >= 0 ? 'success.main' : 'error.main';
  const pnlSign = (portfolio?.pnl_eur ?? 0) >= 0 ? '+' : '';

  // Achsenbeschriftung passend zum Zeitraum: Stunden bei 24h, Datum sonst.
  const formatXAxisTick = (iso: string) => {
    const d = new Date(iso);
    if (timeRange === '24h') {
      return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  };

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Paper-Portfolio</Typography>
            <Typography variant="h4" fontWeight={700}>
              €{(portfolio?.total_eur ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography variant="body2" sx={{ color: pnlColor }}>
              {pnlSign}€{(portfolio?.pnl_eur ?? 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}
              {' '}({pnlSign}{portfolio?.pnl_percent ?? 0}%)
            </Typography>
          </Box>
          <ToggleButtonGroup
            size="small"
            value={timeRange}
            exclusive
            onChange={handleRangeChange}
          >
            <ToggleButton value="24h">24h</ToggleButton>
            <ToggleButton value="7d">7d</ToggleButton>
            <ToggleButton value="30d">30d</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="portfolioGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              tick={{ fontSize: 11 }}
              tickFormatter={formatXAxisTick}
              minTickGap={40}
            />
            <YAxis hide domain={['dataMin - 50', 'dataMax + 50']} />
            <Tooltip
              labelFormatter={(iso: string) =>
                new Date(iso).toLocaleString('de-DE', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })
              }
              formatter={(v: number) => [`€${v.toFixed(2)}`, 'Wert']}
            />
            <Area type="monotone" dataKey="value" stroke="#4CAF50" fill="url(#portfolioGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default PortfolioChart;
